import { Card, Form } from "react-bootstrap"
import { useLocalizedStrings } from "../Localization";
import { useRef, useState, useContext, useEffect } from "react";
import { useAPIUpload, usePost } from "../API";
import { Button, Checkbox, useToast } from "@aidkitorg/component-library";
import { copyToClipboard } from "../Util";
import InterfaceContext, { ConfigurationContext, SupportedLanguage } from "../Context";
import { langToWord } from "../Localization";

export default function PublicS3() {

  const L = useLocalizedStrings();
  const fileInput = useRef(null as any);
  const audioFileInput = useRef(null as any);
  const [fileInputLabel, setFileInputLabel] = useState(L.questions.attachment.choose_files);
  const [audioFileInputLabel, setAudioFileInputLabel] = useState(L.questions.attachment.choose_files);
  const [s3URL, setS3URL] = useState('');
  const [audioS3URL, setAudioS3URL] = useState('');
  const [lang, setLang] = useState<SupportedLanguage>('en');

  const [shortlinkFileUrl, setShortlinkFileUrl] = useState('');
  const [shortlinkSlug, setShortlinkSlug] = useState('')
  const [validSlug, setValidSlug] = useState(false)

  const [shortlinkURL, setShortlinkURL] = useState('')

  const [shortlinkCreationStatus, setShortlinkCreationStatus] = useState<'' | 'success' | 'error'>('')
  const [shortlinkErrorMsg, setShortlinkErrorMsg] = useState('')

  const [overrideShortlinkCode, setOverrideShortlinkCode] = useState(false)

  const uploadURL = usePost("/document/upload_url");
  const upload = useAPIUpload("/upload");
  const createShortlink = usePost("/document/create_shortlink");

  const context = useContext(InterfaceContext);
  const configuration = useContext(ConfigurationContext);

  const { toast } = useToast();
  
  const handleLanguageChange = (selectedLanguage: SupportedLanguage) => {
    setLang(selectedLanguage);
  };
    
  async function uploadFile(files: File[], isAudioUpload: boolean) {
    if (isAudioUpload && !lang) {
      toast({
        description: "Language code is required for audio uploads.",
        variant: 'error'
      });
      return;
    }

    const filePath = isAudioUpload 
      ? `tts/${lang}/${files[0].name}`
      : files[0].name;

    const url = await uploadURL({
      path: filePath,
      length: files[0].size,
      publicFile: true
    }); 
        
    let result;
        
    try {
      result = await upload(files as unknown as any[], {}, url.uploadURL, "PUT");
      if (isAudioUpload) {
        setAudioS3URL(url.savedPath!);
      } else {
        setS3URL(url.savedPath!);
      }
    } catch (e) {
      console.warn("Error uploading file: ", e);
      toast({
        description: "Error uploading file",
        variant: 'error'
      });
    }

    if (typeof result === 'string' && url.savedPath) {
      toast({
        description: "Upload successful",
        variant: 'success'
      });
      if (fileInput.current) { 
        fileInput.current.value = "";
        setFileInputLabel(L.questions.attachment.choose_file);
      } else if (isAudioUpload && audioFileInput.current) {
        audioFileInput.current.value = "";
        setAudioFileInputLabel(L.questions.attachment.choose_file);
      }
    } 
  } 

  const createShortlinkFromFile = async () => {
    const result = await createShortlink({
      file: shortlinkFileUrl,
      slug: shortlinkSlug,
      override: overrideShortlinkCode,
    })

    if (result.status === "ok" && result.shortlink) {
      setShortlinkCreationStatus('success')
      setShortlinkURL(result.shortlink)
    } else {
      setShortlinkCreationStatus('error')
      setShortlinkErrorMsg(result?.message || "")
    }
  }

  // Validity Rules for Shortlink Slugs
  // Shortlink slugs must more than 8 characters in length (to prevent clashing with any auto-generated slugs)
  // Shortlink slugs must be alphanumeric
  useEffect(() => {
    if (shortlinkSlug.length > 8 &&  /^[0-9A-Z]+$/i.test(shortlinkSlug)) {
      setValidSlug(true)
    } else {
      setValidSlug(false)
    }

  }, [shortlinkSlug])

  const shortlinkStatusMessage = () => {
    if (shortlinkCreationStatus === 'success') {
      return (
        <Card style={{ width: '30rem', padding: '2rem', backgroundColor: "lavender" }}>
          <Card.Text id='url'>
            Successfully created shortlink at: 
            <Button variant="link" href={shortlinkURL}>{shortlinkURL}</Button>
          </Card.Text> 
          <Button variant="secondary" onClick={() => { copyToClipboard(shortlinkURL); toast({ description: L.copied_to_clipboard, variant: 'success' }); }}>
            Copy URL to Clipboard
          </Button> 
        </Card>
      )
    } else if (shortlinkCreationStatus === 'error') {
      return (
        <Card 
          className="!bg-red-200"
          style={{ width: '30rem', padding: '1rem' }}>
          <Card.Text>
            Error creating shortlink: {shortlinkErrorMsg}
          </Card.Text> 
        </Card>
      )
    }
  }

  return(
    <>
      <h5>Upload a general document file to store on S3 and create a publicly accessible URL.</h5>
      <div>
        <Form>
          <div className="row">
            <div className="col">
              <Form.Group>
                <div className="custom-file">
                  <input
                    multiple
                    className="custom-file-input"
                    id={"fileupload"}
                    onChange={async (e) => {
                      if (e.target.files && e.target.files.length === 0) {
                        setFileInputLabel(L.questions.attachment.choose_file);
                      } else {
                        if (e.target.files) {
                          uploadFile(Array.from(e.target.files), false);
                          setFileInputLabel(L.questions.attachment.choose_file);
                        }
                      }
                    }}
                    type="file"
                    ref={fileInput}
                  />
                  <label
                    className={"custom-file-label"}
                    htmlFor={"fileupload"}
                  >
                    {fileInputLabel}
                  </label>
                </div>
              </Form.Group>
            </div>
          </div>
        </Form>
        <div hidden={!s3URL}>
          <div className="d-flex justify-content-center m-3">
            <Card style={{ width: '30rem', padding: '2rem', backgroundColor: "lavender" }}>
              <Card.Title>
                Public-Access URL:
              </Card.Title>
              <Card.Text id='url'>
                {s3URL}
              </Card.Text> 
              <Button variant="secondary" onClick={() => { copyToClipboard(s3URL); toast({ description: L.copied_to_clipboard, variant: 'success' }); }}>
                Copy URL to Clipboard
              </Button> 
            </Card>
          </div>
        </div>
      </div>
      <hr/>
      <h5>Custom Shortlink</h5>
      <div>
        <div>
          Shortlink codes must be at least 9 characters and only contain numbers and letters.
        </div>
        <div className="py-2">
          <div>
            <label>
              URL:
              <input
                className="ml-2 px-2 border rounded-md"
                value={shortlinkFileUrl}
                onChange={(e) => setShortlinkFileUrl(e.target.value)}
              />
            </label>
          </div>
          <div>
            <label>
              Shortlink Code:
              <input 
                className="ml-2 px-2 border rounded-md"
                onChange={(e) => setShortlinkSlug(e.target.value)}
              />
            </label>
          </div>
        </div>
        <div>
          Your shortlink will look like: {`${window.location.origin}/sl/${shortlinkSlug}`}
        </div>
        <div className="py-2">
          <Checkbox 
            checked={overrideShortlinkCode}
            onChange={
              (v) => {
                if (v) { 
                  setOverrideShortlinkCode(true) 
                }
                else {
                  setOverrideShortlinkCode(false)
                }
              }
            } 
            label="Override existing shortlinks"/>
        </div>
        <div>
          {
            overrideShortlinkCode && 
            <Card 
              className="!bg-red-200"
              style={{ width: '40rem', padding: '1rem' }}>
              <Card.Text>
                This will override any existing shortlinks created with the same shortlink code.
              </Card.Text> 
            </Card>
          }
        </div>
        <div>
          <Button className="my-2" isDisabled={!validSlug} onClick={async () => createShortlinkFromFile()}>Create Shortlink</Button>        
        </div>
        <div className="d-flex justify-content-center m-3">
          {shortlinkStatusMessage()}
        </div>
      </div>
      <hr/>
      <h5>Upload an audio file to store for custom text-to-speech.</h5>
      <div>
        <Form>
          <div className="row">
            <div className="col">
              <Form.Group>
                <Form.Label>Language:</Form.Label>
                <Form.Control
                  as="select"
                  value={lang}
                  onChange={(e) => setLang(e.target.value as SupportedLanguage)}
                >
                  {(configuration.languages || 'en,es').split(',').map((language: string) => (
                    <option key={language} value={language}>
                      {langToWord(language as SupportedLanguage, 'Name')}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <div className="custom-file">
                  <input />
                  <input
                    multiple
                    className="custom-file-input"
                    id={"audioFileUpload"}
                    onChange={async (e) => {
                      if (e.target.files && e.target.files.length === 0) {
                        setAudioFileInputLabel(L.questions.attachment.choose_file);
                      } else {
                        if (e.target.files) {
                          uploadFile(Array.from(e.target.files), true); // True for audio upload
                          setAudioFileInputLabel(L.questions.attachment.choose_file);
                        }
                      }
                    }}
                    type="file"
                    ref={audioFileInput}
                  />
                  <label
                    className={"custom-file-label"}
                    htmlFor={"audioFileUpload"}
                  >
                    {audioFileInputLabel}
                  </label>
                </div>
              </Form.Group>
            </div>
          </div>
        </Form>
        <div hidden={!audioS3URL}>
          <div className="d-flex justify-content-center m-3">
            <Card style={{ width: '30rem', padding: '2rem', backgroundColor: "lavender" }}>
              <Card.Title>
                Custom Text-to-Speech URL:
              </Card.Title>
              <Card.Text id='url'>
                {audioS3URL}
              </Card.Text> 
              <Button variant="secondary" onClick={() => { copyToClipboard(audioS3URL); toast({ description: L.copied_to_clipboard, variant: 'success' }) }}>
                Copy URL to Clipboard
              </Button> 
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
